import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCloseOnEscape } from '../../hooks/useCloseOnEscape';
import { useClickOutside } from './hooks/useClickOutside';
import ProfileNavigation from '../Profile/ProfileNavigation';
import LogoutConfirmModal from './LogoutConfirmModal';
import { useAuth } from '../../contexts/AuthContext';
import messageThreadsApiService from '../../services/messageThreadsApi';
import {
  buildReadMapFromThreads,
  countUnreadThreads,
  getThreadReadMap,
  hasThreadReadMap,
  setThreadReadMap,
  threadListHasServerUnread,
} from '../../utils/messageReadTracker';
import './HeaderAuth.css';

const HeaderAuth = ({ isLandingPage, currentUser, onLoginClick }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showLogoutModal, setShowLogoutModal] = useState(false);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const { logout, isLoggingOut: authIsLoggingOut, userProfile } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);

  const actorUserId =
    userProfile?.backend_user_id ||
    userProfile?.id ||
    userProfile?.firebase_uid ||
    currentUser?.uid ||
    null;

  useEffect(() => {
    let cancelled = false;
    if (!actorUserId) {
      setUnreadCount(0);
      return () => {};
    }
    const loadUnread = async () => {
      try {
        const threads = await messageThreadsApiService.listThreads({ userId: actorUserId, limit: 50 });
        if (cancelled) return;
        let readMap = getThreadReadMap(actorUserId);
        if (!hasThreadReadMap(actorUserId) && !threadListHasServerUnread(threads)) {
          readMap = buildReadMapFromThreads(threads);
          setThreadReadMap(actorUserId, readMap);
        }
        const count = countUnreadThreads(threads, readMap);
        setUnreadCount(count);
      } catch (err) {
        if (!cancelled) setUnreadCount(0);
      }
    };
    loadUnread();
    const onReadUpdate = () => loadUnread();
    window.addEventListener('message-read-updated', onReadUpdate);
    return () => {
      cancelled = true;
      window.removeEventListener('message-read-updated', onReadUpdate);
    };
  }, [actorUserId]);

  useClickOutside(dropdownRef, () => {
    // Don't close dropdown if logout modal is open
    if (isDropdownOpen && !showLogoutModal) {
      setIsDropdownOpen(false);
    }
  });

  useCloseOnEscape(isDropdownOpen && !showLogoutModal, () => setIsDropdownOpen(false));

  // Get user's first name
  const getFirstName = () => {
    if (!currentUser) return 'User';
    
    if (currentUser.displayName) {
      const firstName = currentUser.displayName.split(' ')[0];
      return firstName || 'User';
    }
    
    // Fallback to email if no display name
    if (currentUser.email) {
      return currentUser.email.split('@')[0];
    }
    
    return 'User';
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleNavigationClick = () => {
    setIsDropdownOpen(false);
  };

  const handleLogoutClick = () => {
    setShowLogoutModal(true);
  };

  const handleConfirmLogout = async () => {
    setShowLogoutModal(false);
    setIsDropdownOpen(false);
    await logout();
    navigate('/');
  };

  const handleCancelLogout = () => {
    setShowLogoutModal(false);
  };

  if (currentUser) {
    return (
      <>
        <div className="header-auth-user-actions">
          <Link
            className={`header-message-btn ${!isLandingPage ? 'header-message-btn-dark' : ''}`}
            to="/messages"
            aria-label="Open messages"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path
                d="M17 8.5H7C6.17 8.5 5.5 9.17 5.5 10V16C5.5 16.83 6.17 17.5 7 17.5H9.2L11.5 19.8C11.79 20.09 12.21 20.09 12.5 19.8L14.8 17.5H17C17.83 17.5 18.5 16.83 18.5 16V10C18.5 9.17 17.83 8.5 17 8.5Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M9 13H15"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
              <circle cx="18.2" cy="6.2" r="2.2" fill="#F6C531" />
            </svg>
            {unreadCount > 0 ? <span className="header-message-badge">{unreadCount > 99 ? '99+' : unreadCount}</span> : null}
          </Link>
          <div className="header-profile-dropdown" ref={dropdownRef}>
          <button
            className={`header-profile-trigger ${!isLandingPage ? 'header-profile-trigger-dark' : ''}`}
            onClick={toggleDropdown}
            aria-label="User menu"
          >
            <div className="header-profile-icon">
              <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="16" cy="16" r="16" fill="#E8F0D7"/>
                <circle cx="16" cy="12" r="4" fill="#94B85F"/>
                <path d="M10 22C10 18 12.686 16 16 16C19.314 16 22 18 22 22V23H10V22Z" fill="#94B85F"/>
              </svg>
            </div>
            <div className="header-profile-text">
              <span className="header-profile-greeting">Hi {getFirstName()}</span>
              <svg 
                width="16" 
                height="16" 
                viewBox="0 0 16 16" 
                fill="none"
                className={`header-profile-arrow ${isDropdownOpen ? 'rotated' : ''}`}
              >
                <path d="M4 6L8 10L12 6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </button>

          {isDropdownOpen && (
            <div className="header-profile-dropdown-menu">
              <ProfileNavigation
                variant="dropdown"
                onNavigate={handleNavigationClick}
                onLogoutClick={handleLogoutClick}
              />
            </div>
          )}
        </div>
        </div>
        
        <LogoutConfirmModal
          isOpen={showLogoutModal}
          onClose={handleCancelLogout}
          onConfirm={handleConfirmLogout}
          isLoggingOut={authIsLoggingOut}
        />
      </>
    );
  }

  return (
    <div className="header-auth-buttons">
      <button
        className={`header-login-btn ${!isLandingPage ? 'header-login-btn--dark' : ''}`}
        onClick={onLoginClick}
      >
        Sign in
      </button>
    </div>
  );
};

export default HeaderAuth;
